import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, Pressable, Modal, ScrollView,
  TextInput, Platform,
} from 'react-native';
import { useSelector } from 'react-redux';
import Svg, { Path } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { DELIVERY_RPM_ICON_OPTIONS } from './components/deliveryRpmIcons';
import HarborDataTable from '../../../harbor/HarborDataTable';
import {
  RouteRoadblock,
  TPL_STATUSES, DUO_STATUSES, ROADBLOCK_TYPES, REASON_CODE_CATEGORIES,
  roadblockFromDTO,
} from './types';
import MultiSelectDropdown from './components/MultiSelectDropdown';
import ConfirmCancelModal from './components/ConfirmCancelModal';
import { t } from './i18n';
import { ListLanguageToggle } from '../PerfectTruckManagement/EquipmentConfig';
import { EquipmentLanguage } from '../PerfectTruckManagement/types';
import FontFamily from '../../../../constants/fonts';
import { useToast } from '../../../../components/LhToastContainer';
import { AdminColors } from '../../constants/adminColors';
import { RootState } from '../../../../store';
import {
  fetchRouteRoadblocks,
  fetchRouteRoadblockById,
  createRouteRoadblock,
  updateRouteRoadblock,
  toggleRouteRoadblock,
  parseError,
} from '../../../../api/deliveryRpmApi';

// ─── Types ────────────────────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc';
type ColFilters = Record<string, string[]>;

// Maps UI column keys to backend sortBy field names
const ROADBLOCK_SORT_FIELD_MAP: Record<string, string> = {
  roadblockType: 'roadblockType',
  routeRoadblock: 'routeRoadblock',
  displayOrder: 'displayOrder',
  locationTypes: 'locationType',
  orgTypes: 'orgType',
  tplStatuses: 'threePlStatus',
  duoStatuses: 'duoStatus',
  active: 'isActive',
};

interface FormState {
  roadblockType: 'Stop Issue' | 'Route Issue' | '';
  routeRoadblock: string;
  displayOrder: string;
  reasonCodeCategory: 'Delivery' | 'Equipment' | 'Sales' | 'Warehouse';
  locationTypes: string[];
  orgTypes: string[];
  tplStatuses: string[];
  duoStatuses: string[];
  pictureRequired: boolean;
  pictureOptional: boolean;
  pictureTitle: string;
  textFieldRequired: boolean;
  textFieldOptional: boolean;
  textFieldTitle: string;
  supervisorNotificationWarning: boolean;
  supervisorAutomaticNotification: boolean;
  icon: string;
}

const EMPTY_FORM: FormState = {
  roadblockType: '',
  routeRoadblock: '',
  displayOrder: '',
  reasonCodeCategory: 'Delivery',
  locationTypes: [],
  orgTypes: [],
  tplStatuses: [],
  duoStatuses: [],
  pictureRequired: false,
  pictureOptional: false,
  pictureTitle: '',
  textFieldRequired: false,
  textFieldOptional: false,
  textFieldTitle: '',
  supervisorNotificationWarning: false,
  supervisorAutomaticNotification: false,
  icon: '',
};

const MAX_ROADBLOCK_LEN = 100;

// ─── Icons ────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth={2.4} strokeLinecap="round" />
  </Svg>
);
const InboxIcon = () => (
  <Svg width={34} height={34} viewBox="0 0 24 24" fill="none">
    <Path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 14H6l2.5-3 1.8 2.1 2.7-3.6L16 17zm1-8H4V5h16v4z" fill="#E0E0E0" />
  </Svg>
);

const Toggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <Pressable
    style={[tog.track, value && tog.trackOn]}
    onPress={() => onChange(!value)}
    accessibilityRole="switch"
    accessibilityState={{ checked: value }}
  >
    <View style={[tog.thumb, value && tog.thumbOn]} />
  </Pressable>
);
const tog = StyleSheet.create({
  track: { width: 38, height: 22, borderRadius: 100, backgroundColor: '#9E9E9E', justifyContent: 'center', paddingHorizontal: 2 },
  trackOn: { backgroundColor: '#027C06' },
  thumb: { width: 18, height: 18, borderRadius: 100, backgroundColor: '#fff', elevation: 1 },
  thumbOn: { marginLeft: 16 },
});

const FormToggleRow = ({ label, value, onChange, hint }: { label: string; value: boolean; onChange: (v: boolean) => void; hint?: string }) => (
  <View style={ftr.row}>
    <Toggle value={value} onChange={onChange} />
    <Text style={ftr.label}>{label}</Text>
    {hint ? <Text style={ftr.hint}> {hint}</Text> : null}
  </View>
);
const ftr = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  label: { fontSize: 14, fontWeight: '500', color: '#2D2D2D', fontFamily: FontFamily.medium },
  hint: { fontSize: 13, color: '#9E9E9E', fontFamily: FontFamily.regular },
});

// ─── Column definitions ───────────────────────────────────────────────────────

const renderRoadblockTypeCell = (value: RouteRoadblock['roadblockType']) => (
  <View style={s.badgeCell}>
    <View style={[s.badge, value === 'Stop Issue' ? s.badgeStop : s.badgeRoute]}>
      <Text style={[s.badgeText, value === 'Stop Issue' ? s.badgeTextStop : s.badgeTextRoute]}>{value}</Text>
    </View>
  </View>
);

const renderTagsCell = (values: string[]) => (
  <View style={s.tagsCell}>
    {values.length === 0 ? (
      <Text style={s.tagEmptyText}>-</Text>
    ) : (
      <View style={s.tagWrap}>
        {values.map((value) => (
          <View key={value} style={s.tag}><Text style={s.tagText}>{value}</Text></View>
        ))}
      </View>
    )}
  </View>
);

const UNICODE_EMOJI_REGEX = /\p{Extended_Pictographic}/u;

const hasUnicodeEmoji = (value: string) => UNICODE_EMOJI_REGEX.test(value);

const renderIconValue = (value: string, size: number, color: string) => {
  if (hasUnicodeEmoji(value)) {
    return <Text style={[s.emojiIcon, { fontSize: size + 2 }]}>{value}</Text>;
  }
  return <MaterialIcons name={value as any} size={size} color={color} />;
};

const renderIconCell = (item: RouteRoadblock) => (
  <View style={{ justifyContent: 'center', alignItems: 'center', minHeight: 48, paddingHorizontal: 12 }}>
    {item.icon ? (
      renderIconValue(item.icon, 20, '#005CBC')
    ) : (
      <Text style={{ fontSize: 13, color: '#B1B1B1' }}>—</Text>
    )}
  </View>
);

const COLS_BASE = [
  {
    columnKey: 'roadblockType',
    flexGrow: 1,
    renderCell: (item: RouteRoadblock) => renderRoadblockTypeCell(item.roadblockType),
  },
  {
    columnKey: 'routeRoadblock',
    flexGrow: 1,
  },
  {
    columnKey: 'icon',
    flexGrow: 0.6,
    sortable: false,
    renderCell: (item: RouteRoadblock) => renderIconCell(item),
  },
  {
    columnKey: 'displayOrder',
    flexGrow: 1,
    getSortValue: (item: RouteRoadblock) => item.displayOrder ?? 0,
  },
  {
    columnKey: 'locationTypes',
    flexGrow: 1,
    renderCell: (item: RouteRoadblock) => renderTagsCell(item.locationTypes),
    getSortValue: (item: RouteRoadblock) => item.locationTypes[0] ?? '',
  },
  {
    columnKey: 'orgTypes',
    flexGrow: 1,
    renderCell: (item: RouteRoadblock) => renderTagsCell(item.orgTypes),
    getSortValue: (item: RouteRoadblock) => item.orgTypes[0] ?? '',
  },
  {
    columnKey: 'tplStatuses',
    flexGrow: 1,
    renderCell: (item: RouteRoadblock) => renderTagsCell(item.tplStatuses),
    getSortValue: (item: RouteRoadblock) => item.tplStatuses[0] ?? '',
  },
  {
    columnKey: 'duoStatuses',
    flexGrow: 1,
    renderCell: (item: RouteRoadblock) => renderTagsCell(item.duoStatuses),
    getSortValue: (item: RouteRoadblock) => item.duoStatuses[0] ?? '',
  },
  {
    columnKey: 'active',
    flexGrow: 1,
    getSortValue: (item: RouteRoadblock) => (item.active ? 1 : 0),
  },
];

// Column headers are resolved per-language inside the component (see COL_HEADER_KEYS below).
const COL_HEADER_KEYS: Record<string, Parameters<typeof t>[1]> = {
  roadblockType: 'table.roadblockType',
  routeRoadblock: 'table.routeRoadblock',
  icon: 'table.icon',
  displayOrder: 'table.displayOrder',
  locationTypes: 'table.locationType',
  orgTypes: 'table.orgType',
  tplStatuses: 'table.3pl',
  duoStatuses: 'table.duo',
  active: 'table.active',
};

// ─── Main component ───────────────────────────────────────────────────────────

const RouteRoadblocksTab: React.FC<{ listLanguage?: EquipmentLanguage }> = ({ listLanguage = 'en' }) => {
  const toast = useToast();
  
  // Load org types and location types from Redux masterdata
  const locationTypes = useSelector((state: RootState) => state.masterData.locationFacilityTypes);
  const orgTypes = useSelector((state: RootState) => state.masterData.orgTypes);

  const [records, setRecords] = useState<RouteRoadblock[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: SortDir }>({
    key: 'displayOrder',
    direction: 'asc',
  });
  const [colFilters, setColFilters] = useState<ColFilters>({});
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isIconDropdownVisible, setIsIconDropdownVisible] = useState(false);
  const [modalLanguage, setModalLanguage] = useState<EquipmentLanguage>('en');
  const [enForm, setEnForm] = useState({ routeRoadblock: '', pictureLabel: '', textLabel: '' });
  const [frForm, setFrForm] = useState({ routeRoadblock: '', pictureLabel: '', textLabel: '' });
  const [esForm, setEsForm] = useState({ routeRoadblock: '', pictureLabel: '', textLabel: '' });
  const [showTranslationError, setShowTranslationError] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const COLS = useMemo(() => COLS_BASE.map((col) => ({
    ...col,
    header: t(listLanguage, COL_HEADER_KEYS[col.columnKey]),
  })), [listLanguage]);

  // ── Load records from API with server-side sorting ────────────────────────
  const loadRecords = useCallback(async (sort: { key: string; direction: SortDir }) => {
    setLoading(true);
    try {
      const backendField = ROADBLOCK_SORT_FIELD_MAP[sort.key];
      const sortBy = backendField
        ? sort.direction === 'desc' ? `-${backendField}` : backendField
        : undefined;
      const params: Record<string, string> = { language: listLanguage };
      if (sortBy) params.sortBy = sortBy;
      const result = await fetchRouteRoadblocks(params);
      setRecords(result.items.map(roadblockFromDTO));
    } catch (err) {
      toast.showToast({ header: 'Error', subtext: parseError(err), variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [listLanguage]);

  // Reload the list (labels re-resolve server-side) whenever the selected language changes.
  useEffect(() => { loadRecords(sortConfig); }, [loadRecords]);

  const getFilterOptions = (col: string): string[] => {
    const vals = new Set<string>();
    records.forEach((r) => {
      const v = r[col as keyof RouteRoadblock];
      if (col === 'active') { vals.add(r.active ? 'Active' : 'Disabled'); return; }
      if (Array.isArray(v)) { (v as string[]).forEach((i) => vals.add(i)); return; }
      vals.add(String(v));
    });
    return [...vals].sort();
  };

  const handleToggleActive = async (id: string, _val: boolean) => {
    try {
      const updated = await toggleRouteRoadblock(Number(id));
      setRecords((prev) => prev.map((r) => (r.id === id ? roadblockFromDTO(updated) : r)));
    } catch (err) {
      toast.showToast({ header: 'Error', subtext: parseError(err), variant: 'error' });
    }
  };

  const rowMatchesFilters = useCallback((
    row: RouteRoadblock,
    filters: ColFilters,
    skipColumn?: string,
  ) => Object.entries(filters).every(([col, sel]) => {
    if (col === skipColumn || sel.length === 0) return true;
    const val = row[col as keyof RouteRoadblock];
    if (col === 'active') return sel.includes(row.active ? 'Active' : 'Disabled');
    if (Array.isArray(val)) return (val as string[]).some((v) => sel.includes(v));
    return sel.includes(String(val));
  }), []);

  const getCrossFilterOptions = useCallback((columnKey: string): string[] => {
    const vals = new Set<string>();
    records
      .filter((row) => rowMatchesFilters(row, colFilters, columnKey))
      .forEach((row) => {
        const val = row[columnKey as keyof RouteRoadblock];
        if (columnKey === 'active') {
          vals.add(row.active ? 'Active' : 'Disabled');
          return;
        }
        if (Array.isArray(val)) {
          (val as string[]).forEach((item) => vals.add(item));
          return;
        }
        vals.add(String(val));
      });
    return [...vals].sort();
  }, [records, colFilters, rowMatchesFilters]);

  const tableColumns = useMemo(() => {
    const getOptions = (values: string[]) => values.map((value) => ({ label: value, value }));

    return COLS.map((col) => ({
      ...col,
      sortable: col.columnKey !== 'active',
      filterOptions: col.columnKey === 'roadblockType'
        ? // keep raw values as `value` and show translated labels
          getCrossFilterOptions(col.columnKey).map((v) => ({ label: v === 'Stop Issue' ? t(listLanguage, 'roadblockType.stopIssue') : t(listLanguage, 'roadblockType.routeIssue'), value: v }))
        : getOptions(getCrossFilterOptions(col.columnKey)),
      renderCell:
        col.columnKey === 'active'
          ? (item: RouteRoadblock) => (
              <View style={s.toggleCell}>
                <Toggle value={item.active} onChange={(value) => handleToggleActive(item.id, value)} />
                <Text style={[s.toggleLabel, item.active && s.toggleLabelOn]}>
                  {item.active ? t(listLanguage, 'label.active') : t(listLanguage, 'label.disabled')}
                </Text>
              </View>
            )
          : col.columnKey === 'roadblockType'
            ? (item: RouteRoadblock) => {
                const translated = item.roadblockType === 'Stop Issue'
                  ? t(listLanguage, 'roadblockType.stopIssue')
                  : t(listLanguage, 'roadblockType.routeIssue');
                return (
                  <View style={s.badgeCell}>
                    <View style={[s.badge, item.roadblockType === 'Stop Issue' ? s.badgeStop : s.badgeRoute]}>
                      <Text style={[s.badgeText, item.roadblockType === 'Stop Issue' ? s.badgeTextStop : s.badgeTextRoute]}>{translated}</Text>
                    </View>
                  </View>
                );
              }
            : col.renderCell,
    }));
  }, [getCrossFilterOptions, COLS, listLanguage]);

  const filteredRecords = useMemo(() => {
    return records.filter((row) => rowMatchesFilters(row, colFilters));
  }, [records, colFilters, rowMatchesFilters]);

  // Keep current page within range when filtered records change
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [filteredRecords, page, pageSize]);

  const pagedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  const sortedCount = filteredRecords.length;

  const anyFilterActive = Object.values(colFilters).some((v) => v.length > 0);

  const normalize = (v: string) => v.trim().toLowerCase();

  const filteredIconOptions = useMemo(() => {
    const query = normalize(form.icon);
    if (!query) return DELIVERY_RPM_ICON_OPTIONS;
    return DELIVERY_RPM_ICON_OPTIONS.filter((iconName) => normalize(iconName).includes(query));
  }, [form.icon]);

  const handleSort = (key: string, direction: SortDir) => {
    const next = { key, direction };
    setSortConfig(next);
    setPage(1);
    loadRecords(next);
  };

  const handleColumnFilter = (columnKey: string, values: string[]) => {
    setColFilters((prev) => ({ ...prev, [columnKey]: values }));
    setPage(1);
  };

  const clearAllFilters = () => { setColFilters({}); setPage(1); };

  // Duplicate check for create: roadblockType + routeRoadblock must be unique.
  // The routeRoadblock text is entered per-language (enForm/frForm/esForm) on create;
  // the English value is the canonical `routeRoadblock` sent to the backend.
  const isDuplicate = (() => {
    const normalized = (modalMode === 'create' ? enForm.routeRoadblock : form.routeRoadblock).trim().toLowerCase();
    if (!normalized) return false;
    if (modalMode === 'create') {
      return records.some(
        r => r.roadblockType === form.roadblockType && r.routeRoadblock.toLowerCase() === normalized
      );
    }
    if (modalMode === 'edit' && editId) {
      return records.some(
        r => r.id !== editId && r.roadblockType === form.roadblockType && r.routeRoadblock.toLowerCase() === normalized
      );
    }
    return false;
  })();

  const parsedDisplayOrder = form.displayOrder.trim() !== '' ? Number(form.displayOrder) : null;
  const isDisplayOrderDuplicate = parsedDisplayOrder != null && records.some((r) =>
    r.displayOrder === parsedDisplayOrder && (modalMode !== 'edit' || r.id !== editId)
  );

  // Mutual exclusion: pictureRequired and pictureOptional cannot both be true
  const isPictureMutuallyExclusive = form.pictureRequired && form.pictureOptional;
  // Mutual exclusion: textFieldRequired and textFieldOptional cannot both be true
  const isTextFieldMutuallyExclusive = form.textFieldRequired && form.textFieldOptional;

  // Field label validation: if photo or text field is enabled, check if all 3 language labels are filled
  const arePhotoLabelsComplete =
    !form.pictureRequired && !form.pictureOptional
      ? true // not required if field is disabled
      : (enForm?.pictureLabel ?? '').trim() !== '' &&
        (frForm?.pictureLabel ?? '').trim() !== '' &&
        (esForm?.pictureLabel ?? '').trim() !== '';

  const areTextLabelsComplete =
    !form.textFieldRequired && !form.textFieldOptional
      ? true // not required if field is disabled
      : (enForm?.textLabel ?? '').trim() !== '' &&
        (frForm?.textLabel ?? '').trim() !== '' &&
        (esForm?.textLabel ?? '').trim() !== '';

  // Current-language label values (defensive fallback avoids crashes if a form object is ever partial)
  const currentPictureLabel = modalLanguage === 'en' ? (enForm?.pictureLabel ?? '') : modalLanguage === 'fr' ? (frForm?.pictureLabel ?? '') : (esForm?.pictureLabel ?? '');
  const currentTextLabel = modalLanguage === 'en' ? (enForm?.textLabel ?? '') : modalLanguage === 'fr' ? (frForm?.textLabel ?? '') : (esForm?.textLabel ?? '');

  const isFormValid = modalMode === 'edit'
    ? form.icon.trim() !== '' &&
      form.displayOrder.trim() !== '' &&
      form.locationTypes.length > 0 &&
      form.orgTypes.length > 0 &&
      form.tplStatuses.length > 0 &&
      form.duoStatuses.length > 0 &&
      !isDisplayOrderDuplicate &&
      !isDuplicate &&
      !isPictureMutuallyExclusive &&
      !isTextFieldMutuallyExclusive
    : form.roadblockType !== '' &&
      enForm.routeRoadblock.trim() !== '' &&
      enForm.routeRoadblock.trim().length <= MAX_ROADBLOCK_LEN &&
      form.icon.trim() !== '' &&
      form.displayOrder.trim() !== '' &&
      form.locationTypes.length > 0 &&
      form.orgTypes.length > 0 &&
      form.tplStatuses.length > 0 &&
      form.duoStatuses.length > 0 &&
      !isDisplayOrderDuplicate &&
      !isDuplicate &&
      !isPictureMutuallyExclusive &&
      !isTextFieldMutuallyExclusive;

  const nextDisplayOrder = useMemo(() => {
    if (records.length === 0) return 1;
    const max = Math.max(...records.map(r => r.displayOrder ?? 0));
    return max + 1;
  }, [records]);

  const handleOpenCreate = () => {
    setForm({ ...EMPTY_FORM, displayOrder: String(nextDisplayOrder) });
    setSubmitError(null);
    setEditId(null);
    setModalMode('create');
    setModalLanguage('en');
    setEnForm({ routeRoadblock: '', pictureLabel: '', textLabel: '' });
    setFrForm({ routeRoadblock: '', pictureLabel: '', textLabel: '' });
    setEsForm({ routeRoadblock: '', pictureLabel: '', textLabel: '' });
    setShowTranslationError(false);
    setAttemptedSubmit(false);
  };

  const handleOpenEdit = (rec: RouteRoadblock) => {
    setForm({
      roadblockType: rec.roadblockType,
      routeRoadblock: rec.routeRoadblock,
      displayOrder: rec.displayOrder != null ? String(rec.displayOrder) : '',
      reasonCodeCategory: rec.reasonCodeCategory,
      locationTypes: [...rec.locationTypes],
      orgTypes: [...rec.orgTypes],
      tplStatuses: [...rec.tplStatuses],
      duoStatuses: [...rec.duoStatuses],
      pictureRequired: rec.pictureRequired,
      pictureOptional: rec.pictureOptional,
      pictureTitle: rec.pictureTitle,
      textFieldRequired: rec.textFieldRequired,
      textFieldOptional: rec.textFieldOptional,
      textFieldTitle: rec.textFieldTitle,
      supervisorNotificationWarning: rec.supervisorNotificationWarning,
      supervisorAutomaticNotification: rec.supervisorAutomaticNotification,
      icon: rec.icon,
    });
    setSubmitError(null);
    setEditId(rec.id);
    setModalMode('edit');
    setAttemptedSubmit(false);
    // Initialize language forms with fallback values
    setEnForm({ routeRoadblock: rec.routeRoadblock, pictureLabel: '', textLabel: '' });
    setFrForm({ routeRoadblock: rec.routeRoadblock, pictureLabel: '', textLabel: '' });
    setEsForm({ routeRoadblock: rec.routeRoadblock, pictureLabel: '', textLabel: '' });
    setModalLanguage(listLanguage);
    (async () => {
      try {
        const [en, fr, es] = await Promise.all([
          fetchRouteRoadblockById(Number(rec.id), 'en'),
          fetchRouteRoadblockById(Number(rec.id), 'fr'),
          fetchRouteRoadblockById(Number(rec.id), 'es'),
        ]);
        setEnForm({ routeRoadblock: en.routeRoadblock, pictureLabel: en.pictureTitle || '', textLabel: en.textFieldTitle || '' });
        setFrForm({ routeRoadblock: fr.routeRoadblock, pictureLabel: fr.pictureTitle || '', textLabel: fr.textFieldTitle || '' });
        setEsForm({ routeRoadblock: es.routeRoadblock, pictureLabel: es.pictureTitle || '', textLabel: es.textFieldTitle || '' });
      } catch {
        // Keep the seeded fallback values if per-language fetch fails.
      }
    })();
  };

  const handleSave = async () => {
    setAttemptedSubmit(true);
    if (!isFormValid) return;
    if (!arePhotoLabelsComplete || !areTextLabelsComplete) return;
    setSubmitError(null);
    // On create, require translations for all languages
    if (modalMode === 'create') {
      if (!enForm.routeRoadblock.trim() || !frForm.routeRoadblock.trim() || !esForm.routeRoadblock.trim()) {
        setShowTranslationError(true);
        return;
      }
    }
    try {
      const translations = [
        { languageCode: 'en', label: enForm.routeRoadblock.trim() },
        { languageCode: 'fr', label: frForm.routeRoadblock.trim() },
        { languageCode: 'es', label: esForm.routeRoadblock.trim() },
      ];

      // Build field label translations only if photo/text fields are enabled
      const fieldLabelTranslations = [];
      if (form.pictureRequired || form.pictureOptional) {
        fieldLabelTranslations.push(
          { languageCode: 'en', pictureTitleLabel: (enForm.pictureLabel ?? '').trim() },
          { languageCode: 'fr', pictureTitleLabel: (frForm.pictureLabel ?? '').trim() },
          { languageCode: 'es', pictureTitleLabel: (esForm.pictureLabel ?? '').trim() }
        );
      }
      if (form.textFieldRequired || form.textFieldOptional) {
        fieldLabelTranslations.push(
          { languageCode: 'en', textFieldTitleLabel: (enForm.textLabel ?? '').trim() },
          { languageCode: 'fr', textFieldTitleLabel: (frForm.textLabel ?? '').trim() },
          { languageCode: 'es', textFieldTitleLabel: (esForm.textLabel ?? '').trim() }
        );
      }

      if (modalMode === 'create') {
        const parsedOrder = form.displayOrder !== '' ? Number(form.displayOrder) : undefined;
        const payload = {
          roadblockType: form.roadblockType as 'Stop Issue' | 'Route Issue',
          translations,
          ...(fieldLabelTranslations.length > 0 && { fieldLabelTranslations }),
          reasonCodeCategory: form.reasonCodeCategory,
          iconName: form.icon || undefined,
          displayOrder: parsedOrder,
          locationTypes: form.locationTypes,
          orgTypes: form.orgTypes,
          threePlStatuses: form.tplStatuses,
          duoStatuses: form.duoStatuses,
          pictureRequired: form.pictureRequired,
          pictureOptional: form.pictureOptional,
          pictureTitle: form.pictureTitle.trim(),
          textFieldRequired: form.textFieldRequired,
          textFieldOptional: form.textFieldOptional,
          textFieldTitle: form.textFieldTitle.trim(),
          supervisorNotificationWarning: form.supervisorNotificationWarning,
          supervisorAutomaticNotification: form.supervisorAutomaticNotification,
        };
        const dto = await createRouteRoadblock(payload);
        setRecords(prev => [...prev, roadblockFromDTO(dto)]);
        toast.showToast({ header: t(listLanguage, 'toast.routeAddSuccess'), subtext: '', variant: 'success' });
      } else if (modalMode === 'edit' && editId) {
        const parsedOrder = form.displayOrder !== '' ? Number(form.displayOrder) : undefined;
        const payload = {
          roadblockType: form.roadblockType as 'Stop Issue' | 'Route Issue',
          translations,
          ...(fieldLabelTranslations.length > 0 && { fieldLabelTranslations }),
          reasonCodeCategory: form.reasonCodeCategory,
          iconName: form.icon || undefined,
          displayOrder: parsedOrder,
          locationTypes: form.locationTypes,
          orgTypes: form.orgTypes,
          threePlStatuses: form.tplStatuses,
          duoStatuses: form.duoStatuses,
          pictureRequired: form.pictureRequired,
          pictureOptional: form.pictureOptional,
          pictureTitle: form.pictureTitle.trim(),
          textFieldRequired: form.textFieldRequired,
          textFieldOptional: form.textFieldOptional,
          textFieldTitle: form.textFieldTitle.trim(),
          supervisorNotificationWarning: form.supervisorNotificationWarning,
          supervisorAutomaticNotification: form.supervisorAutomaticNotification,
        };
        const dto = await updateRouteRoadblock(Number(editId), payload);
        setRecords(prev => prev.map(r => r.id === editId ? roadblockFromDTO(dto) : r));
        toast.showToast({ header: t(listLanguage, 'toast.routeUpdateSuccess'), subtext: '', variant: 'success' });
      }
      setModalMode(null);
    } catch (err) {
      const message = parseError(err);
      setSubmitError(message);
      toast.showToast({ header: 'Error', subtext: message, variant: 'error' });
    }
  };

  const handleCancelClick = () => setShowCancelConfirm(true);
  const handleConfirmDiscard = () => { setShowCancelConfirm(false); setSubmitError(null); setModalMode(null); };
  const handleDismissConfirm = () => setShowCancelConfirm(false);

  const updateForm = (patch: Partial<FormState>) => {
    if (submitError) setSubmitError(null);
    setForm(prev => ({ ...prev, ...patch }));
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <View style={s.root}>
      {/* Top bar */}
      <View style={s.topBar}>
        <Text style={s.summary}>
          {t(listLanguage, 'summary.showingRoute').replace('{countFiltered}', String(sortedCount)).replace('{countTotal}', String(records.length))}
        </Text>
        <View style={s.topActions}>
          {anyFilterActive && (
            <Pressable style={s.clearBtn} onPress={clearAllFilters}>
              <Text style={s.clearBtnText}>{t(listLanguage, 'button.clearFilters')}</Text>
            </Pressable>
          )}
          <Pressable style={s.createBtn} onPress={handleOpenCreate}>
            <PlusIcon />
            <Text style={s.createBtnText}>{t(listLanguage, 'addButton.route')}</Text>
          </Pressable>
        </View>
      </View>

      <View style={s.tableWrap}>
        {!loading && filteredRecords.length === 0 ? (
          <View style={s.emptyWrap}>
            <InboxIcon />
            <Text style={s.emptyText}>
              {anyFilterActive
                ? t(listLanguage, 'empty.noRecordsFilters')
                : t(listLanguage, 'empty.noRecordsRoute')}
            </Text>
            {anyFilterActive && (
              <Pressable style={s.emptyBtn} onPress={clearAllFilters}>
                <Text style={s.emptyBtnText}>{t(listLanguage, 'button.clearFilters')}</Text>
              </Pressable>
            )}
          </View>
        ) : (
          <HarborDataTable
            data={pagedRecords}
            columns={tableColumns}
            tableHeader={t(listLanguage, 'tabs.routeRoadblocks')}
            tableDescription={t(listLanguage, 'tabs.routeRoadblocks')}
            maxRecordsToShow={pageSize}
            onRowClick={handleOpenEdit}
            onSort={handleSort}
            externalPage={page - 1}
            externalTotalPages={Math.max(1, Math.ceil(filteredRecords.length / pageSize))}
            onExternalPageChange={(nextPage) => setPage(nextPage + 1)}
            onPageSizeChange={(nextSize) => {
              setPageSize(nextSize);
              setPage(1);
            }}
            showHeaderTopBorder={false}
            columnFilters={colFilters}
            onColumnFilter={handleColumnFilter}
            isLoading={loading}
            locale={{
              rowsPerPageLabel: t(listLanguage, 'pagination.rowsPerPage'),
              pageLabel: t(listLanguage, 'pagination.page'),
              pageOfLabel: t(listLanguage, 'pagination.of'),
              firstPageLabel: t(listLanguage, 'pagination.first'),
              prevPageLabel: t(listLanguage, 'pagination.previous'),
              nextPageLabel: t(listLanguage, 'pagination.next'),
              lastPageLabel: t(listLanguage, 'pagination.last'),
            }}
          />
        )}
      </View>

      {/* ── Create / Edit Modal ── */}
      <Modal visible={modalMode !== null} transparent animationType="fade">
        <View style={s.backdrop}>
          <View style={s.formModal}>
            {/* Header */}
            <View style={s.modalHeader}>
              <Text style={s.modalTitle}>
                {modalMode === 'create' ? t(modalLanguage, 'modal.route.addTitle') : t(modalLanguage, 'modal.route.editTitle')}
              </Text>
              <Pressable style={s.modalClose} onPress={handleCancelClick}>
                <Text style={s.modalCloseText}>✕</Text>
              </Pressable>
            </View>

            {/* Body */}
            <ScrollView style={s.modalBodyScroll} contentContainerStyle={s.modalBody} showsVerticalScrollIndicator={false}>
              {/* Modal language toggle + validation error */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <View style={{ flex: 1 }}>
                  {showTranslationError ? (
                    <View style={[s.modalErrorBox]}>
                      <MaterialIcons name="error-outline" size={16} color={AdminColors.red400} />
                      <Text style={s.modalErrorText}>{t(modalLanguage, 'validation.completeTranslations')}</Text>
                    </View>
                  ) : null}
                </View>
                <View style={{ marginLeft: 12 }}>
                  <ListLanguageToggle value={modalLanguage} onChange={(l) => { setModalLanguage(l); setShowTranslationError(false); }} compact />
                </View>
              </View>

              {/* Roadblock Type */}
              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.roadblockType')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                <View style={s.typeOpts}>
                  {ROADBLOCK_TYPES.map(rt => (
                    <Pressable
                      key={rt}
                      style={[s.typeOpt, form.roadblockType === rt && s.typeOptSel]}
                      onPress={() => updateForm({ roadblockType: rt })}
                    >
                      <Text style={[s.typeOptText, form.roadblockType === rt && s.typeOptTextSel]}>
                        {t(modalLanguage, rt === 'Stop Issue' ? 'roadblockType.stopIssue' : 'roadblockType.routeIssue')}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>

              {/* Route Roadblock */}
              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.routeRoadblock')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                {modalMode === 'edit' ? (
                  <View style={s.readonlyField}>
                    <Text style={s.readonlyText}>
                      {modalLanguage === 'en' ? enForm.routeRoadblock : modalLanguage === 'fr' ? frForm.routeRoadblock : esForm.routeRoadblock}
                    </Text>
                  </View>
                ) : (
                  <>
                    <TextInput
                      style={[s.textInput, isDuplicate && s.textInputError]}
                      value={modalLanguage === 'en' ? enForm.routeRoadblock : modalLanguage === 'fr' ? frForm.routeRoadblock : esForm.routeRoadblock}
                      onChangeText={val => {
                        if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, routeRoadblock: val }));
                        else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, routeRoadblock: val }));
                        else setEsForm(prev => ({ ...prev, routeRoadblock: val }));
                        setSubmitError(null);
                        setShowTranslationError(false);
                      }}
                      placeholder={t(modalLanguage, 'modal.form.routeRoadblockPlaceholder')}
                      placeholderTextColor="#B1B1B1"
                      maxLength={MAX_ROADBLOCK_LEN}
                    />
                    <View style={s.inputMeta}>
                      {isDuplicate && (
                        <Text style={s.errorText}>{t(modalLanguage, 'validation.roadblockDuplicate')}</Text>
                      )}
                      <Text style={[s.counter, (modalLanguage === 'en' ? enForm.routeRoadblock : modalLanguage === 'fr' ? frForm.routeRoadblock : esForm.routeRoadblock).length >= MAX_ROADBLOCK_LEN && s.counterWarn]}>
                        {(modalLanguage === 'en' ? enForm.routeRoadblock : modalLanguage === 'fr' ? frForm.routeRoadblock : esForm.routeRoadblock).length}/{MAX_ROADBLOCK_LEN}
                      </Text>
                    </View>
                  </>
                )}
              </View>

              {/* Display Order */}
              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.displayOrder')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                <TextInput
                  style={[s.textInput, isDisplayOrderDuplicate && s.textInputError]}
                  value={form.displayOrder}
                  onChangeText={val => updateForm({ displayOrder: val.replace(/[^0-9]/g, '') })}
                  placeholder={String(nextDisplayOrder)}
                  placeholderTextColor="#B1B1B1"
                  keyboardType="numeric"
                />
                {isDisplayOrderDuplicate && (
                  <Text style={s.errorText}>{t(modalLanguage, 'validation.displayOrderDuplicate')}</Text>
                )}
              </View>

              {/* Reason Code Category */}
              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.reasonCodeCategory')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                <View style={s.typeOpts}>
                  {REASON_CODE_CATEGORIES.map((category) => (
                    <Pressable
                      key={category}
                      style={[s.typeOpt, form.reasonCodeCategory === category && s.typeOptSel]}
                      onPress={() => updateForm({ reasonCodeCategory: category as any })}
                    >
                      <Text
                        style={[
                          s.typeOptText,
                          form.reasonCodeCategory === category && s.typeOptTextSel,
                        ]}
                      >
                        {t(modalLanguage, `category.${category.toLowerCase()}` as any)}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>

              {/* Multi-selects */}
              <MultiSelectDropdown
                label={t(modalLanguage, 'modal.form.locationType')}
                required
                options={locationTypes}
                selected={form.locationTypes}
                onChange={v => updateForm({ locationTypes: v })}
                placeholder={t(modalLanguage, 'modal.form.selectPlaceholder')}
                selectAllLabel={t(modalLanguage, 'modal.form.selectAll')}
                clearAllLabel={t(modalLanguage, 'modal.form.clearAll')}
              />
              <MultiSelectDropdown
                label={t(modalLanguage, 'modal.form.orgType')}
                required
                options={orgTypes}
                selected={form.orgTypes}
                onChange={v => updateForm({ orgTypes: v })}
                placeholder={t(modalLanguage, 'modal.form.selectPlaceholder')}
                selectAllLabel={t(modalLanguage, 'modal.form.selectAll')}
                clearAllLabel={t(modalLanguage, 'modal.form.clearAll')}
              />
              <MultiSelectDropdown
                label={t(modalLanguage, 'modal.form.3plStatus')}
                required
                options={TPL_STATUSES}
                selected={form.tplStatuses}
                onChange={v => updateForm({ tplStatuses: v })}
                placeholder={t(modalLanguage, 'modal.form.selectPlaceholder')}
                selectAllLabel={t(modalLanguage, 'modal.form.selectAll')}
                clearAllLabel={t(modalLanguage, 'modal.form.clearAll')}
              />
              <MultiSelectDropdown
                label={t(modalLanguage, 'modal.form.duoStatus')}
                required
                options={DUO_STATUSES}
                selected={form.duoStatuses}
                onChange={v => updateForm({ duoStatuses: v })}
                placeholder={t(modalLanguage, 'modal.form.selectPlaceholder')}
                selectAllLabel={t(modalLanguage, 'modal.form.selectAll')}
                clearAllLabel={t(modalLanguage, 'modal.form.clearAll')}
              />
              {/* Icon */}
              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.icon')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                <View style={s.iconInputRow}>
                  <View style={s.iconPreviewWrap}>
                    {renderIconValue(form.icon || 'image', 18, '#005CBC')}
                  </View>
                  <TextInput
                    style={[s.textInput, s.flex1]}
                    value={form.icon}
                    onChangeText={val => updateForm({ icon: val })}
                    placeholder={t(modalLanguage, 'modal.form.iconPlaceholder')}
                    placeholderTextColor="#B1B1B1"
                    autoCapitalize="none"
                    autoCorrect={false}
                    onFocus={() => setIsIconDropdownVisible(true)}
                    onBlur={() => { setTimeout(() => setIsIconDropdownVisible(false), 200); }}
                  />
                </View>
                {isIconDropdownVisible ? (
                  <View style={s.iconDropdownMenu}>
                    {filteredIconOptions.length > 0 ? (
                      <ScrollView style={s.iconDropdownList} keyboardShouldPersistTaps="handled">
                        {filteredIconOptions.map((iconName) => {
                          const selected = form.icon.trim().toLowerCase() === iconName.trim().toLowerCase();
                          return (
                            <Pressable
                              key={iconName}
                              style={[s.iconDropdownOption, selected && s.iconDropdownOptionActive]}
                              onPressIn={() => {
                                updateForm({ icon: iconName });
                                setIsIconDropdownVisible(false);
                              }}
                            >
                              <MaterialIcons name={iconName as any} size={18} color={selected ? '#005CBC' : '#4B4B4B'} />
                              <Text style={[s.iconDropdownOptionText, selected && s.iconDropdownOptionTextActive]}>
                                {iconName}
                              </Text>
                            </Pressable>
                          );
                        })}
                      </ScrollView>
                    ) : (
                      <Text style={s.iconDropdownEmptyText}>{t(modalLanguage, 'modal.form.iconEmptyHint')}</Text>
                    )}
                  </View>
                ) : null}
              </View>

              {/* Optional Additional Fields */}
              <View style={s.optionalSection}>
                <Text style={s.optionalHeader}>Optional Additional Fields</Text>

                {/* Photo Field */}
                <View style={s.toggleGroup}>
                  <Text style={s.toggleGroupLabel}>Photo Field</Text>
                  <View style={s.toggleGroupRows}>
                    <FormToggleRow
                      label="Required"
                      value={form.pictureRequired}
                      onChange={v => updateForm({ pictureRequired: v, ...(v ? { pictureOptional: false } : {}) })}
                    />
                    <FormToggleRow
                      label="Optional"
                      value={form.pictureOptional}
                      onChange={v => updateForm({ pictureOptional: v, ...(v ? { pictureRequired: false } : {}) })}
                    />
                  </View>
                  {isPictureMutuallyExclusive && (
                    <Text style={s.errorText}>Photo field cannot be both Required and Optional.</Text>
                  )}
                  {(form.pictureRequired || form.pictureOptional) && (
                    <View style={s.titleInputWrap}>
                      <Text style={s.titleInputLabel}>{t(modalLanguage, 'modal.form.photoFieldLabel')}</Text>
                      <TextInput
                        style={[
                          s.textInput,
                          attemptedSubmit && currentPictureLabel.trim() === '' &&
                          (form.pictureRequired || form.pictureOptional) &&
                          s.textInputError
                        ]}
                        value={currentPictureLabel}
                        onChangeText={t => {
                          if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, pictureLabel: t }));
                          else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, pictureLabel: t }));
                          else setEsForm(prev => ({ ...prev, pictureLabel: t }));
                        }}
                        placeholder={modalLanguage === 'en' ? 'e.g. Attach Issue Photo' : modalLanguage === 'fr' ? 'ex. Joindre une photo du problème' : 'p.ej. Adjuntar foto del problema'}
                        placeholderTextColor="#B1B1B1"
                        maxLength={200}
                      />
                      {attemptedSubmit && !arePhotoLabelsComplete && (form.pictureRequired || form.pictureOptional) && (
                        <Text style={s.errorText}>{t(modalLanguage, 'validation.fieldLabelAllLanguagesRequired')}</Text>
                      )}
                    </View>
                  )}
                </View>

                {/* Text Field */}
                <View style={s.toggleGroup}>
                  <Text style={s.toggleGroupLabel}>Text Field</Text>
                  <View style={s.toggleGroupRows}>
                    <FormToggleRow
                      label="Required"
                      value={form.textFieldRequired}
                      onChange={v => updateForm({ textFieldRequired: v, ...(v ? { textFieldOptional: false } : {}) })}
                    />
                    <FormToggleRow
                      label="Optional"
                      value={form.textFieldOptional}
                      onChange={v => updateForm({ textFieldOptional: v, ...(v ? { textFieldRequired: false } : {}) })}
                    />
                  </View>
                  {isTextFieldMutuallyExclusive && (
                    <Text style={s.errorText}>Text field cannot be both Required and Optional.</Text>
                  )}
                  {(form.textFieldRequired || form.textFieldOptional) && (
                    <View style={s.titleInputWrap}>
                      <Text style={s.titleInputLabel}>{t(modalLanguage, 'modal.form.textFieldLabel')}</Text>
                      <TextInput
                        style={[
                          s.textInput,
                          attemptedSubmit && currentTextLabel.trim() === '' &&
                          (form.textFieldRequired || form.textFieldOptional) &&
                          s.textInputError
                        ]}
                        value={currentTextLabel}
                        onChangeText={t => {
                          if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, textLabel: t }));
                          else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, textLabel: t }));
                          else setEsForm(prev => ({ ...prev, textLabel: t }));
                        }}
                        placeholder={modalLanguage === 'en' ? 'e.g. Describe the Issue' : modalLanguage === 'fr' ? 'ex. Décrivez le problème' : 'p.ej. Describir el problema'}
                        placeholderTextColor="#B1B1B1"
                        maxLength={200}
                      />
                      {attemptedSubmit && !areTextLabelsComplete && (form.textFieldRequired || form.textFieldOptional) && (
                        <Text style={s.errorText}>{t(modalLanguage, 'validation.fieldLabelAllLanguagesRequired')}</Text>
                      )}
                    </View>
                  )}
                </View>

                {/* Supervisor Notifications */}
                <View style={s.toggleGroup}>
                  <Text style={s.toggleGroupLabel}>Supervisor Notifications</Text>
                  <View style={s.toggleGroupRows}>
                    <FormToggleRow
                      label="Notification Warning"
                      value={form.supervisorNotificationWarning}
                      onChange={v => updateForm({
                        supervisorNotificationWarning: v,
                        ...(v ? { supervisorAutomaticNotification: false } : {}),
                      })}
                    />
                    <FormToggleRow
                      label="Automatic Notification"
                      value={form.supervisorAutomaticNotification}
                      onChange={v => updateForm({
                        supervisorAutomaticNotification: v,
                        ...(v ? { supervisorNotificationWarning: false } : {}),
                      })}
                    />
                  </View>
                </View>
                <Text style={s.optionalHeader}>{t(modalLanguage, 'modal.form.optionalFields')}</Text>
                <FormToggleRow
                  label={t(modalLanguage, 'modal.form.pictureRequirement')}
                  value={form.pictureRequired}
                  onChange={v => updateForm({ pictureRequired: v })}
                  hint={t(modalLanguage, 'modal.form.noByDefault')}
                />
                <FormToggleRow
                  label={t(modalLanguage, 'modal.form.textField')}
                  value={form.textFieldRequired}
                  onChange={v => updateForm({ textFieldRequired: v })}
                  hint={t(modalLanguage, 'modal.form.noByDefault')}
                />
              </View>

              {submitError ? (
                <View style={s.modalErrorBox}>
                  <Text style={s.modalErrorText}>{submitError}</Text>
                </View>
              ) : null}
            </ScrollView>

            {/* Footer */}
            <View style={s.modalFooter}>
              <Pressable style={s.btnSecondary} onPress={handleCancelClick}>
                <Text style={s.btnSecondaryText}>{t(modalLanguage, 'button.cancel')}</Text>
              </Pressable>
              <Pressable
                style={[s.btnPrimary, !isFormValid && s.btnPrimaryDisabled]}
                onPress={handleSave}
                disabled={!isFormValid}
              >
                <Text style={[s.btnPrimaryText, !isFormValid && s.btnPrimaryTextDisabled]}>{t(modalLanguage, 'button.save')}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Cancel Confirm Modal ── */}
      <ConfirmCancelModal
        visible={showCancelConfirm}
        onConfirmDiscard={handleConfirmDiscard}
        onGoBack={handleDismissConfirm}
        title={t(listLanguage, 'modal.unsaved.title')}
        message={t(listLanguage, 'modal.unsaved.message')}
        cancelLabel={t(listLanguage, 'modal.unsaved.cancel')}
        confirmLabel={t(listLanguage, 'modal.unsaved.confirm')}
      />
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: AdminColors.bgPage },

  topBar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 12, padding: 16, paddingBottom: 12,
  },
  summary: { fontSize: 13, color: '#616161', fontFamily: FontFamily.regular },
  bold: { fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  topActions: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  clearBtn: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 24, borderWidth: 1.5, borderColor: '#005CBC' },
  clearBtnText: { fontSize: 13, fontWeight: '700', color: '#005CBC', fontFamily: FontFamily.bold },
  createBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    height: 40, paddingHorizontal: 24, borderRadius: 24,
    backgroundColor: '#034895',
  },
  createBtnText: { fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },

  tableWrap: { flex: 1, backgroundColor: '#fff' },
  badgeCell: { justifyContent: 'center', minHeight: 48, paddingHorizontal: 12, paddingVertical: 8 },
  badge: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignSelf: 'center',
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeStop: { backgroundColor: '#DAEEFA' },
  badgeRoute: { backgroundColor: '#F9E0CA' },
  badgeText: { fontSize: 12, fontWeight: '500', fontFamily: FontFamily.medium },
  badgeTextStop: { color: '#01346F' },
  badgeTextRoute: { color: '#4D2E0C' },
  tagsCell: { justifyContent: 'center', minHeight: 48, paddingHorizontal: 12, paddingVertical: 8 },
  tagWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  tag: { backgroundColor: '#DAEEFA', borderRadius: 12, paddingHorizontal: 6, paddingVertical: 2 },
  tagText: { fontSize: 11, fontWeight: '500', color: '#01346F', fontFamily: FontFamily.medium },
  tagEmptyText: { fontSize: 13, color: '#9E9E9E', fontFamily: FontFamily.regular },
  toggleCell: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 48, paddingHorizontal: 12 },
  toggleLabel: { fontSize: 12, fontWeight: '500', color: '#9E9E9E', fontFamily: FontFamily.medium },
  toggleLabelOn: { color: '#027C06' },

  emptyWrap: { padding: 48, alignItems: 'center', gap: 12 },
  emptyText: { fontSize: 14, color: '#9E9E9E', textAlign: 'center', maxWidth: 400, fontFamily: FontFamily.regular },
  emptyBtn: { backgroundColor: '#005CBC', borderRadius: 24, paddingHorizontal: 20, paddingVertical: 8, marginTop: 4 },
  emptyBtnText: { fontSize: 14, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },

  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  formModal: { width: '100%', maxWidth: 560, maxHeight: '90%', backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 20, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#F0F0F0' },
  modalTitle: { fontSize: 20, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  modalClose: { width: 30, height: 30, alignItems: 'center', justifyContent: 'center', borderRadius: 6 },
  modalCloseText: { fontSize: 16, color: '#9E9E9E' },
  modalBodyScroll: { flex: 1 },
  modalBody: { padding: 20, gap: 16 },
  modalErrorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: AdminColors.red75,
    borderRadius: 8,
    padding: 12,
  },
  modalErrorText: { fontSize: 13, color: AdminColors.red400, fontFamily: FontFamily.medium, flex: 1 },
  modalFooter: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, padding: 20, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#F0F0F0' },

  field: { gap: 6 },
  fieldLabelRow: { flexDirection: 'row', alignItems: 'center' },
  fieldLabel: { fontSize: 14, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  req: { fontSize: 14, color: '#C12C01', fontFamily: FontFamily.bold },
  textInput: { height: 40, borderWidth: 1, borderColor: '#D0D5DD', borderRadius: 6, paddingHorizontal: 12, fontSize: 14, color: '#2D2D2D', backgroundColor: '#fff', fontFamily: FontFamily.regular },
  textInputError: { borderColor: '#C12C01' },
  inputMeta: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 },
  errorText: { fontSize: 12, color: '#C12C01', fontFamily: FontFamily.medium, flex: 1 },
  counter: { fontSize: 12, color: '#9E9E9E', fontFamily: FontFamily.regular },
  counterWarn: { color: '#C12C01', fontWeight: '700' },
  readonlyField: { height: 40, borderWidth: 1, borderColor: '#E8EAED', borderRadius: 6, paddingHorizontal: 12, justifyContent: 'center', backgroundColor: '#F9FAFB' },
  readonlyText: { fontSize: 14, color: '#616161', fontFamily: FontFamily.regular },

  optionalSection: { gap: 14, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#F0F0F0' },
  optionalHeader: { fontSize: 13, fontWeight: '700', color: '#616161', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: FontFamily.bold },
  toggleGroup: { gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: '#F0F0F0', backgroundColor: '#FAFAFA' },
  toggleGroupLabel: { fontSize: 13, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  toggleGroupRows: { gap: 10 },
  titleInputWrap: { gap: 6, marginTop: 4 },
  titleInputLabel: { fontSize: 13, fontWeight: '500', color: '#616161', fontFamily: FontFamily.medium },
  titleInputHint: { fontSize: 12, color: '#9E9E9E', fontFamily: FontFamily.regular },
  langInputGroup: { gap: 6 },
  langLabel: { fontSize: 12, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },

  // Icon field
  flex1: { flex: 1 },
  iconInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  iconPreviewWrap: {
    width: 38, height: 38, borderRadius: 6, borderWidth: 1, borderColor: '#D0D5DD',
    backgroundColor: '#F9FAFB', alignItems: 'center', justifyContent: 'center',
  },
  iconDropdownMenu: {
    borderWidth: 1, borderColor: '#D0D5DD', borderRadius: 6,
    backgroundColor: '#fff', marginBottom: 8,
  },
  iconDropdownList: { maxHeight: 220 },
  iconDropdownOption: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 10, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#F0F0F0',
  },
  iconDropdownOptionActive: { backgroundColor: '#F3F7FD' },
  iconDropdownOptionText: { fontSize: 12, color: '#4B4B4B', fontFamily: FontFamily.regular },
  iconDropdownOptionTextActive: { color: '#005CBC', fontFamily: FontFamily.medium },
  iconDropdownEmptyText: { fontSize: 12, color: '#9E9E9E', fontFamily: FontFamily.regular, paddingHorizontal: 10, paddingVertical: 10 },
  emojiIcon: { color: '#2D2D2D', lineHeight: 20 },

  typeOpts: { flexDirection: 'row', gap: 10 },
  typeOpt: { flex: 1, paddingVertical: 10, borderRadius: 8, borderWidth: 1.5, borderColor: '#E0E0E0', alignItems: 'center' },
  typeOptSel: { backgroundColor: '#005CBC', borderColor: '#005CBC' },
  typeOptText: { fontSize: 14, fontWeight: '600', color: '#9E9E9E', fontFamily: FontFamily.medium },
  typeOptTextSel: { color: '#fff' },

  btnSecondary: { height: 40, paddingHorizontal: 22, borderRadius: 24, borderWidth: 1.5, borderColor: '#005CBC', alignItems: 'center', justifyContent: 'center' },
  btnSecondaryText: { fontSize: 14, fontWeight: '700', color: '#005CBC', fontFamily: FontFamily.bold },
  btnPrimary: { height: 40, paddingHorizontal: 22, borderRadius: 24, backgroundColor: '#005CBC', alignItems: 'center', justifyContent: 'center' },
  btnPrimaryDisabled: { backgroundColor: '#F5F5F5' },
  btnPrimaryText: { fontSize: 14, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },
  btnPrimaryTextDisabled: { color: '#BDBDBD' },
});

export default RouteRoadblocksTab;
